import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ListChecks, ArrowRight, Sparkles, Shuffle, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const categories = ["All", "Aptitude", "Reasoning", "Technical", "Verbal", "AML & CTF", "Financial Crime", "Medical Billing", "AR Calling", "SAP MM"];

function AdaptivePractice() {
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["practice-topics"], queryFn: async () => (await api.get("/practice/topics")).data });
  const cats = data?.categories || [];
  const companies = data?.companies || [];
  const [category, setCategory] = useState("");
  const [company, setCompany] = useState("any");
  const [loading, setLoading] = useState(false);

  const start = async () => {
    if (!category) { toast.error("Pick a topic area first."); return; }
    setLoading(true);
    try {
      const payload = { category, count: 10 };
      if (company && company !== "any") payload.company = company;
      const { data: res } = await api.post("/practice/generate", payload);
      navigate(`/quizzes/${res.quiz_id}`);
    } catch {
      toast.error("Could not generate a test for this selection.");
    } finally { setLoading(false); }
  };

  return (
    <div className="p-6 rounded-md border border-primary/30 bg-primary/5 relative overflow-hidden" data-testid="adaptive-practice">
      <div className="absolute -top-8 -right-8 w-32 h-32 bg-primary/10 rounded-full blur-2xl" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-1">
          <Shuffle className="w-5 h-5 text-primary" />
          <h3 className="font-heading font-bold text-lg">Adaptive Practice</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Get a fresh set of questions every attempt, tuned to real MNC test patterns.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="rounded-md sm:w-56" data-testid="adaptive-category"><SelectValue placeholder="Choose topic area" /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={company} onValueChange={setCompany}>
            <SelectTrigger className="rounded-md sm:w-52" data-testid="adaptive-company"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any company</SelectItem>
              {companies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={start} disabled={loading} className="rounded-md gap-2" data-testid="adaptive-start">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4" /> Start Fresh Test</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Quizzes() {
  const [cat, setCat] = useState("All");
  const { data, isLoading } = useQuery({
    queryKey: ["quizzes"],
    queryFn: async () => (await api.get("/quizzes")).data,
  });

  const quizzes = (data?.quizzes || []).filter((q) => cat === "All" || q.category === cat);

  return (
    <div className="space-y-6" data-testid="quizzes-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">Practice</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Assessments</h1>
        <p className="text-muted-foreground mt-1">MCQ tests with live timer, instant scoring and explanations.</p>
      </div>

      <AdaptivePractice />

      <h2 className="font-heading font-bold text-lg pt-2">Fixed Assessments</h2>
      <div className="flex flex-wrap gap-2">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            data-testid={`filter-${c}`}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              cat === c ? "bg-primary text-primary-foreground" : "bg-card border border-border hover:bg-secondary"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : quizzes.length === 0 ? (
        <p className="text-muted-foreground py-12">No assessments in this category yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {quizzes.map((q) => (
            <div key={q.id} className="p-6 rounded-md border border-border bg-card flex flex-col hover:-translate-y-1 transition-transform" data-testid={`quiz-card-${q.id}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                  <ListChecks className="w-5 h-5 text-primary" />
                </div>
                {q.ai_generated && (
                  <span className="inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded bg-primary/10 text-primary">
                    <Sparkles className="w-3 h-3" /> AI
                  </span>
                )}
              </div>
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">{q.category} · {q.difficulty}</span>
              <h3 className="font-heading font-bold text-lg mb-1">{q.title}</h3>
              <p className="text-sm text-muted-foreground mb-6 flex-1">{q.num_questions} questions</p>
              <Link to={`/quizzes/${q.id}`}>
                <Button className="w-full rounded-md gap-2" data-testid={`start-quiz-${q.id}`}>
                  Start Test <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
